'use client';

import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import type { GameContentEntry, GameId, GameScoreDto } from '@vuganawe/types';
import { useT } from '@/i18n/client';
import { getGameContent, getGameScores, getMyGameContent, submitGameResult } from '@/lib/api/public';
import { SENTENCES, WORD_PAIRS } from '@/content/games';
import {
  guestGameScores,
  recordGuestGameRound,
} from '@/lib/gamification';
import { SentenceBuilder } from '@/components/learning/games/SentenceBuilder';
import { SoundToggle } from '@/components/learning/games/SoundToggle';
import { WordGuess } from '@/components/learning/games/WordGuess';
import type { GameFinish } from '@/components/learning/games/WordMatch';
import { WordMatch } from '@/components/learning/games/WordMatch';
import { WordRush } from '@/components/learning/games/WordRush';
import { DashboardShell, useDashboard } from './DashboardShell';

const GAMES: { id: GameId; icon: string }[] = [
  { id: 'word-match', icon: '🃏' },
  { id: 'word-rush', icon: '⚡' },
  { id: 'sentence-builder', icon: '🧩' },
  { id: 'word-guess', icon: '🔤' },
];

/** i18n key stem per game id. */
const GAME_KEY: Record<GameId, string> = {
  'word-match': 'wordMatch',
  'word-rush': 'wordRush',
  'sentence-builder': 'sentenceBuilder',
  'word-guess': 'wordGuess',
};

function LearningGames({ token, guest = false }: { token: string | null; guest?: boolean }) {
  const t = useT();
  const [scores, setScores] = useState<Record<string, GameScoreDto>>({});
  const [active, setActive] = useState<GameId | null>(null);
  const [lastXp, setLastXp] = useState<number | null>(null);
  // Per-game admin settings; null until loaded (then all games on, static bank).
  const [games, setGames] = useState<Record<GameId, GameContentEntry> | null>(null);

  useEffect(() => {
    // Signed-in learners get content drawn from their own lessons; guests get
    // the global/admin content.
    const load = token ? getMyGameContent(token) : getGameContent();
    load
      .then((content) => setGames(content.games))
      .catch(() => {
        /* offline or unset — every game stays on with the static bank */
      });
  }, [token]);

  // A game is shown unless an admin explicitly turned it off.
  const isEnabled = (id: GameId) => (games ? games[id]?.enabled !== false : true);
  // Each game uses its own admin content, or the built-in bank when too sparse.
  const pairsFor = (id: GameId) => {
    const custom = games?.[id]?.pairs ?? [];
    return custom.length >= 4 ? custom : WORD_PAIRS;
  };
  const sentencesFor = (id: GameId) => {
    const custom = games?.[id]?.sentences ?? [];
    return custom.length >= 2 ? custom : SENTENCES;
  };

  useEffect(() => {
    if (!token) {
      setScores(guestGameScores());
      return;
    }
    getGameScores(token)
      .then((list) => {
        const map: Record<string, GameScoreDto> = {};
        for (const score of list) map[score.game] = score;
        setScores(map);
      })
      .catch(() => {
        /* offline — best scores just won't show */
      });
  }, [token]);

  const finish = useCallback(
    (game: GameId, result: GameFinish) => {
      if (!token) {
        const local = recordGuestGameRound({
          game,
          correct: result.correct,
          total: result.total,
          score: result.score,
        });
        setScores(guestGameScores());
        setLastXp(local.xpAwarded);
        return;
      }
      submitGameResult(token, game, result.correct, result.total, result.score)
        .then((res) => {
          setScores((prev) => ({ ...prev, [game]: { game, best: res.best, plays: res.plays } }));
          setLastXp(res.xpAwarded);
        })
        .catch(() => {
          /* offline — the round still played, XP just isn't recorded */
        });
    },
    [token],
  );

  if (active) {
    const onFinish = (result: GameFinish) => finish(active, result);
    return (
      <div className="vn-games__play">
        <div className="vn-games__playhead">
          <button
            type="button"
            className="vn-btn vn-btn--ghost vn-btn--small"
            onClick={() => {
              setActive(null);
              setLastXp(null);
            }}
          >
            ‹ {t('games.backToGames')}
          </button>
          <span className="vn-games__playtitle">{t(`games.${GAME_KEY[active]}.title`)}</span>
          <span className="vn-games__playhead-right">
            {lastXp !== null ? <span className="vn-games__xp">+{lastXp} XP</span> : null}
            <SoundToggle />
          </span>
        </div>
        {active === 'word-match' ? <WordMatch onFinish={onFinish} pairs={pairsFor('word-match')} /> : null}
        {active === 'word-rush' ? <WordRush onFinish={onFinish} pairs={pairsFor('word-rush')} /> : null}
        {active === 'sentence-builder' ? (
          <SentenceBuilder onFinish={onFinish} bank={sentencesFor('sentence-builder')} />
        ) : null}
        {active === 'word-guess' ? <WordGuess onFinish={onFinish} pairs={pairsFor('word-guess')} /> : null}
        {guest && lastXp !== null ? (
          <div className="vn-games__guest-save" role="status">
            <div>
              <strong>{t('games.guestEarned', { xp: lastXp })}</strong>
              <span>{t('games.guestSaveCopy')}</span>
            </div>
            <Link href="/account/register?next=/account/learn/games" className="vn-btn vn-btn--primary vn-btn--small">
              {t('games.saveXp')}
            </Link>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {guest ? (
        <div className="vn-games__guest-intro">
          <div>
            <strong>{t('games.guestTitle')}</strong>
            <span>{t('games.guestCopy')}</span>
          </div>
          <Link href="/account/register?next=/account/learn/games" className="vn-btn vn-btn--secondary vn-btn--small">
            {t('common.createFreeAccount')}
          </Link>
        </div>
      ) : null}
      <div className="vn-games__toolbar">
        <SoundToggle />
      </div>
      <div className="vn-games__grid">
        {GAMES.filter((game) => isEnabled(game.id)).map((game) => {
          const best = scores[game.id]?.best ?? 0;
          return (
            <button
              key={game.id}
              type="button"
              className="vn-games__card"
              onClick={() => {
                setActive(game.id);
                setLastXp(null);
              }}
            >
              <span className="vn-games__card-icon" aria-hidden="true">
                {game.icon}
              </span>
              <span className="vn-games__card-title">{t(`games.${GAME_KEY[game.id]}.title`)}</span>
              <span className="vn-games__card-desc">{t(`games.${GAME_KEY[game.id]}.desc`)}</span>
              <span className="vn-games__card-best">
                {best > 0 ? t('games.best', { score: best }) : t('games.notPlayed')}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function GamesContent() {
  const { token } = useDashboard();
  return <LearningGames token={token} />;
}

export function GamesScreen() {
  const t = useT();
  return (
    <DashboardShell active="games" title={t('games.title')} subtitle={t('games.subtitle')}>
      <GamesContent />
    </DashboardShell>
  );
}

export function GuestGamesScreen() {
  const t = useT();
  return (
    <div className="vn-catalog">
      <section className="vn-catalog__hero">
        <div className="vn-container">
          <h1 className="vn-catalog__title">{t('games.guestPageTitle')}</h1>
          <p className="vn-catalog__lede">{t('games.guestPageCopy')}</p>
        </div>
      </section>
      <section className="vn-section vn-section--tight">
        <div className="vn-container">
          <LearningGames token={null} guest />
        </div>
      </section>
    </div>
  );
}
